import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { BiArrowToLeft, BiEdit, BiCheck, BiX, BiUser, BiCreditCard, BiBuilding } from 'react-icons/bi'
import { userService, User as UserType } from '../services/userService'
import toast from 'react-hot-toast'
import Alert from '../components/Alert'

interface EditableUserData {
  firstName: string
  lastName: string
  phone: string
  isActive: boolean
}

interface ValidationErrors {
  firstName?: string
  lastName?: string
  phone?: string
}

const UserDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [user, setUser] = useState<UserType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('')
  const [editData, setEditData] = useState<EditableUserData>({
    firstName: '',
    lastName: '',
    phone: '',
    isActive: true
  })
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Get initial tab from URL params, default to 'overview'
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['overview', 'billing']
    return validTabs.includes(tabFromUrl || '') ? tabFromUrl : 'overview'
  }

  const [activeTab, setActiveTabState] = useState(getInitialTab())

  // Function to update active tab and URL params
  const setActiveTab = (tabId: string) => {
    setActiveTabState(tabId)
    const newSearchParams = new URLSearchParams(searchParams)
    if (tabId === 'overview') {
      // Remove tab param for overview (default)
      newSearchParams.delete('tab')
    } else {
      newSearchParams.set('tab', tabId)
    }
    setSearchParams(newSearchParams, { replace: true })
  }

  // Sync tab state when URL changes (browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    const validTabs = ['overview', 'billing']
    const newTab = validTabs.includes(tabFromUrl || '') ? tabFromUrl : 'overview'
    if (newTab !== activeTab) {
      setActiveTabState(newTab || 'overview')
    }
  }, [searchParams, activeTab])

  // Fetch user data
  useEffect(() => {
    if (id) {
      fetchUser()
    }
  }, [id])

  const fetchUser = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const userData = await userService.getUserById(id!)

      if (!userData) {
        throw new Error('User not found')
      }

      setUser(userData)
      setEditData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        isActive: userData.isActive !== false // Default to true if not explicitly false
      })
    } catch (err) {
      console.error('Error fetching user:', err)
      setError('Failed to load user details. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Validation functions
  const validateField = (field: keyof EditableUserData, value: string | boolean): string | null => {
    switch (field) {
      case 'firstName':
        if (typeof value === 'string' && value.trim().length > 0 && value.trim().length < 2) {
          return 'First name must be at least 2 characters long'
        }
        return null

      case 'lastName':
        if (typeof value === 'string' && value.trim().length > 0 && value.trim().length < 2) {
          return 'Last name must be at least 2 characters long'
        }
        return null

      case 'phone':
        if (typeof value === 'string' && value.trim().length > 0) {
          const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
          if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
            return 'Please enter a valid phone number'
          }
        }
        return null

      default:
        return null
    }
  }

  const validateAllFields = (): boolean => {
    const errors: ValidationErrors = {}
    let isValid = true

    // Validate each field
    Object.keys(editData).forEach((key) => {
      const field = key as keyof EditableUserData
      const error = validateField(field, editData[field])
      if (error) {
        errors[field] = error
        isValid = false
      }
    })

    setValidationErrors(errors)
    return isValid
  }

  const handleInputChange = (field: keyof EditableUserData, value: string | boolean) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }))

    // Real-time validation
    const error = validateField(field, value)
    setValidationErrors(prev => ({
      ...prev,
      [field]: error || undefined
    }))

    // Clear save error when user starts typing
    if (message) {
      setMessage('')
      setMessageType('')
    }
  }

  const handleSave = async () => {
    if (!user || !user.id) return

    // Clear previous messages
    setMessage('')
    setMessageType('')

    // Validate all fields
    if (!validateAllFields()) {
      setMessage('Please fix the validation errors before saving.')
      setMessageType('error')
      return
    }

    try {
      setIsSaving(true)

      const updatedUser = await userService.updateUser(user.id, {
        firstName: editData.firstName || undefined,
        lastName: editData.lastName || undefined,
        phone: editData.phone || undefined,
        isActive: editData.isActive
      })

      setUser(updatedUser)
      setIsEditing(false)
      setValidationErrors({})
      setMessage('User updated successfully!')
      setMessageType('success')

      setTimeout(() => {
        setMessage('')
        setMessageType('')
      }, 3000)
    } catch (err: any) {
      console.error('Error updating user:', err)
      setMessage(err?.message || 'Failed to update user. Please try again.')
      setMessageType('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setEditData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        isActive: user.isActive
      })
    }
    setIsEditing(false)
    setMessage('')
    setMessageType('')
    setValidationErrors({})
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const getRoleBadge = (role: string) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${role === 'admin'
        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
        {role === 'admin' ? 'Admin' : 'User'}
      </span>
    )
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BiUser },
    { id: 'billing', label: 'Billing', icon: BiCreditCard },
  ]

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchUser}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 mr-3"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/users')}
                  className="inline-flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <BiArrowToLeft size={16} />
                  <span>Back to Users</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user || !user.id) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            User Not Found
          </h1>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <BiArrowToLeft size={18} />
            <span>Back to Users</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Alert */}
      {message && messageType && (
        <Alert
          type={messageType}
          message={message}
          onClose={() => {
            setMessage('')
            setMessageType('')
          }}
        />
      )}

      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 mb-6 group"
        >
          <BiArrowToLeft size={20} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
          <span className="font-medium">Back to Users</span>
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.firstName || user?.lastName || 'User Details'
              }
            </h1>
            <div className="mt-2 space-y-1">
              <p className="text-slate-600 dark:text-slate-400">
                Email: <span className="font-medium text-slate-800 dark:text-slate-200">{user?.email || 'No email'}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-8">
        <nav className="flex space-x-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-1 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-md font-medium text-sm transition-all duration-200 min-w-0 flex-1 justify-center ${activeTab === tab.id
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-sm border border-slate-200 dark:border-slate-600'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/50'
                  }`}
              >
                <Icon size={18} className={activeTab === tab.id ? 'text-primary-600 dark:text-primary-400' : ''} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Basic Information */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 md:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Basic Information
                </h3>
                <div className="flex items-center space-x-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <BiX size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={
                          isSaving ||
                          Object.keys(validationErrors).some(key => validationErrors[key as keyof ValidationErrors])
                        }
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isSaving ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <BiCheck size={16} />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <BiEdit size={16} />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={isEditing ? editData.firstName : (user?.firstName || '')}
                    onChange={isEditing ? (e) => handleInputChange('firstName', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.firstName
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter first name" : ""}
                  />
                  {isEditing && validationErrors.firstName && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={isEditing ? editData.lastName : (user?.lastName || '')}
                    onChange={isEditing ? (e) => handleInputChange('lastName', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.lastName
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter last name" : ""}
                  />
                  {isEditing && validationErrors.lastName && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.lastName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={isEditing ? editData.phone : (user?.phone || '')}
                    onChange={isEditing ? (e) => handleInputChange('phone', e.target.value) : undefined}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isEditing
                      ? validationErrors.phone
                        ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                      }`}
                    placeholder={isEditing ? "Enter phone number" : (!user?.phone ? "Not provided" : "")}
                  />
                  {isEditing && validationErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Status
                  </label>
                  {isEditing ? (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">Active</span>
                    </label>
                  ) : (
                    <div className="px-3 py-2">
                      {getStatusBadge(user.isActive)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  System Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Email
                    </label>
                    <div className="px-3 py-2 text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-700 rounded-lg">
                      {user?.email || 'No email'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Role
                    </label>
                    <div className="px-3 py-2">
                      {getRoleBadge(user?.role || 'user')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Auth ID
                    </label>
                    <div className="px-3 py-2 text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-700 rounded-lg">
                      {user?.authId || 'Not provided'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Created
                    </label>
                    <div className="px-3 py-2 text-slate-900 dark:text-white">
                      {user?.createdAt ? formatDate(user.createdAt) : 'Unknown'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Last Updated
                    </label>
                    <div className="px-3 py-2 text-slate-900 dark:text-white">
                      {user?.updatedAt ? formatDate(user.updatedAt) : 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Billing Information
                </h3>
              </div>

              <div className="text-center py-8">
                <div className="text-slate-500 dark:text-slate-400 mb-4">
                  <BiCreditCard size={48} className="mx-auto mb-4" />
                  <p className="text-lg font-medium">Billing information will be available here</p>
                  <p className="text-sm">This section will show user's billing details, subscriptions, and payment history.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserDetails 